<script setup>
// Reklam kreatifleri: listeleme, ayrinti, medya, olusturma ve ad guncelleme.
//
// Kreatif adi tek basina hangi gorselin/metnin yayinda oldugunu anlatmiyor;
// bu yuzden ayrinti ve medya araclari satir eylemi olarak duruyor. Yanlis
// kreatifi kopyalayip para harcamanin en sik sebebi bu korluk.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { aracCalistir, diziyeCevir } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
});

const { t } = useI18n();
const route = useRoute();

const satirlar = ref([]);
const yukleniyor = ref(false);
const hata = ref('');

// Ayrinti/medya ayni panelde gosteriliyor: ikisi de "sectigim kreatif neye
// benziyor" sorusunun parcasi, iki ayri pencere acmak gereksiz.
const panelAcik = ref(false);
const panelBaslik = ref('');
const panelVeri = ref(null);
const panelYukleniyor = ref(false);

const formAcik = ref(false);
const kaydediliyor = ref(false);
const form = ref({
  name: '',
  page_id: '',
  link_url: '',
  message: '',
  headline: '',
  image_hash: '',
});

const sutunlar = computed(() => [
  { anahtar: 'id', baslik: t('META_ADS.KREATIFLER.SUTUN.ID') },
  { anahtar: 'name', baslik: t('META_ADS.KREATIFLER.SUTUN.AD') },
  { anahtar: 'status', baslik: t('META_ADS.KREATIFLER.SUTUN.DURUM') },
  { anahtar: 'object_type', baslik: t('META_ADS.KREATIFLER.SUTUN.TUR') },
  { anahtar: 'title', baslik: t('META_ADS.KREATIFLER.SUTUN.BASLIK') },
  { anahtar: 'body', baslik: t('META_ADS.KREATIFLER.SUTUN.METIN') },
]);

// Panelde gosterilen nesnede gorsel varsa onizleme cikarilir; Meta alan adini
// araca gore degistirdigi icin birkac ihtimal birden taraniyor.
const panelGorsel = computed(() => {
  const v = panelVeri.value;
  if (!v || typeof v !== 'object') return '';
  return v.thumbnail_url || v.image_url || v.url || v.permalink_url || '';
});

// Panelde ham JSON basiliyordu: kullanici "object_story_spec" gibi API alan
// adlarini ve suslu parantezleri goruyordu. Meta'nin alan adlari sabit oldugu
// icin bilinenleri Turkce etikete ceviriyoruz; bilinmeyen alan oldugu gibi
// kaliyor - alan adini gizlemek veriyi kaybetmek olurdu.
const ALAN_ETIKETLERI = {
  id: 'META_ADS.KREATIFLER.ALAN.ID',
  name: 'META_ADS.KREATIFLER.ALAN.AD',
  status: 'META_ADS.KREATIFLER.ALAN.DURUM',
  object_type: 'META_ADS.KREATIFLER.ALAN.TUR',
  title: 'META_ADS.KREATIFLER.ALAN.BASLIK',
  body: 'META_ADS.KREATIFLER.ALAN.METIN',
  message: 'META_ADS.KREATIFLER.ALAN.MESAJ',
  link_url: 'META_ADS.KREATIFLER.ALAN.BAGLANTI',
  link_description: 'META_ADS.KREATIFLER.ALAN.BAGLANTI_ACIKLAMA',
  call_to_action_type: 'META_ADS.KREATIFLER.ALAN.EYLEM_DUGMESI',
  image_url: 'META_ADS.KREATIFLER.ALAN.GORSEL_ADRESI',
  image_hash: 'META_ADS.KREATIFLER.ALAN.GORSEL_HASH',
  thumbnail_url: 'META_ADS.KREATIFLER.ALAN.ONIZLEME',
  video_id: 'META_ADS.KREATIFLER.ALAN.VIDEO_ID',
  permalink_url: 'META_ADS.KREATIFLER.ALAN.YAYIN_BAGLANTISI',
  page_id: 'META_ADS.KREATIFLER.ALAN.SAYFA_ID',
  account_id: 'META_ADS.KREATIFLER.ALAN.HESAP_ID',
  instagram_actor_id: 'META_ADS.KREATIFLER.ALAN.INSTAGRAM_HESABI',
  object_story_id: 'META_ADS.KREATIFLER.ALAN.GONDERI_ID',
  object_story_spec: 'META_ADS.KREATIFLER.ALAN.GONDERI_TANIMI',
  effective_object_story_id: 'META_ADS.KREATIFLER.ALAN.YAYINDAKI_GONDERI',
  asset_feed_spec: 'META_ADS.KREATIFLER.ALAN.VARLIK_AKISI',
  url_tags: 'META_ADS.KREATIFLER.ALAN.URL_ETIKETLERI',
  created_time: 'META_ADS.KREATIFLER.ALAN.OLUSTURMA',
  updated_time: 'META_ADS.KREATIFLER.ALAN.GUNCELLEME',
};

const TARIH_ALANLARI = ['created_time', 'updated_time'];

const tarihBicimle = deger => {
  const tarih = new Date(deger);
  return Number.isNaN(tarih.getTime())
    ? String(deger)
    : tarih.toLocaleString('tr-TR');
};

const etiketle = anahtar =>
  ALAN_ETIKETLERI[anahtar] ? t(ALAN_ETIKETLERI[anahtar]) : anahtar;

// Ic ice nesne/dizi tek satira sigmaz; sayisini soyleyip paneli okunur
// tutuyoruz. Bos donen alan hic gosterilmiyor - "—" dolu bir alan izlenimi
// verir, oysa Meta o alani hic gondermemistir.
const degerBicimle = (anahtar, deger) => {
  if (deger === null || deger === undefined || deger === '') return '';
  if (typeof deger === 'boolean') {
    return deger
      ? t('META_ADS.KREATIFLER.PANEL.EVET')
      : t('META_ADS.KREATIFLER.PANEL.HAYIR');
  }
  if (Array.isArray(deger)) {
    return deger.length
      ? t('META_ADS.KREATIFLER.PANEL.OGE_SAYISI', { sayi: deger.length })
      : '';
  }
  if (typeof deger === 'object') {
    const sayi = Object.keys(deger).length;
    return sayi ? t('META_ADS.KREATIFLER.PANEL.ALAN_SAYISI', { sayi }) : '';
  }
  if (TARIH_ALANLARI.includes(anahtar)) return tarihBicimle(deger);
  return String(deger);
};

// Arac kimi zaman tek nesne kimi zaman dizi dondurur; ikisi de ayni
// etiket/deger kalibina giriyor ki sablonda iki ayri dal olmasin.
const panelKayitlari = computed(() => {
  const v = panelVeri.value;
  if (!v || typeof v !== 'object') return [];
  return (Array.isArray(v) ? v : [v])
    .filter(kayit => kayit && typeof kayit === 'object')
    .map(kayit =>
      Object.entries(kayit)
        .map(([anahtar, deger]) => ({
          anahtar,
          etiket: etiketle(anahtar),
          deger: degerBicimle(anahtar, deger),
        }))
        .filter(alan => alan.deger !== '')
    )
    .filter(kayit => kayit.length);
});

// Arac yalnizca metin ozeti urettiyse onu oldugu gibi gosteriyoruz.
const panelMetni = computed(() =>
  typeof panelVeri.value === 'string' ? panelVeri.value : ''
);

const panelBos = computed(
  () => !panelMetni.value && !panelKayitlari.value.length
);

const yukle = async () => {
  if (!props.hesap) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(
      route.params.accountId,
      'ads_get_ad_creatives',
      { account_id: props.hesap }
    );
    satirlar.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    satirlar.value = [];
  } finally {
    yukleniyor.value = false;
  }
};

const paneliAc = async (satir, arac, baslik) => {
  panelAcik.value = true;
  panelBaslik.value = baslik;
  panelVeri.value = null;
  panelYukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, arac, {
      creative_id: satir.id,
    });
    // veri bos donebilir (arac yalnizca metin ozeti uretmis olabilir);
    // o durumda ozet gosterilsin, kullanici bos pencereyle kalmasin.
    panelVeri.value = sonuc.veri ?? sonuc.ozet ?? null;
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    panelAcik.value = false;
  } finally {
    panelYukleniyor.value = false;
  }
};

const ayrintiAc = satir =>
  paneliAc(satir, 'ads_get_creative_details', t('META_ADS.KREATIFLER.AYRINTI_BASLIK'));

const medyaAc = satir =>
  paneliAc(satir, 'ads_get_creative_media', t('META_ADS.KREATIFLER.MEDYA_BASLIK'));

const adiDegistir = async satir => {
  const yeniAd = window.prompt(t('META_ADS.KREATIFLER.AD_SOR'), satir.name || '');
  if (!yeniAd || yeniAd === satir.name) return;
  // Yazma islemi: kreatif yayindaki reklama bagli olabilir, onay sart.
  if (!window.confirm(t('META_ADS.KREATIFLER.GUNCELLE_ONAY', { ad: yeniAd }))) return;
  try {
    await aracCalistir(
      route.params.accountId,
      'ads_update_ad_creative',
      { creative_id: satir.id, name: yeniAd },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.KREATIFLER.GUNCELLENDI'));
    await yukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  }
};

const formuSifirla = () => {
  form.value = {
    name: '',
    page_id: '',
    link_url: '',
    message: '',
    headline: '',
    image_hash: '',
  };
};

const kreatifOlustur = async () => {
  if (!props.hesap) return;
  if (!window.confirm(t('META_ADS.KREATIFLER.OLUSTUR_ONAY'))) return;
  kaydediliyor.value = true;
  hata.value = '';
  try {
    // Bos alanlar hic gonderilmiyor: Meta bos string'i "bu alani temizle"
    // diye yorumlayip dogrulama hatasi dondurebiliyor.
    const argumanlar = { account_id: props.hesap };
    Object.entries(form.value).forEach(([anahtar, deger]) => {
      if (String(deger || '').trim()) argumanlar[anahtar] = String(deger).trim();
    });
    await aracCalistir(
      route.params.accountId,
      'ads_create_ad_creative',
      argumanlar,
      { onaylandi: true }
    );
    useAlert(t('META_ADS.KREATIFLER.OLUSTU'));
    formAcik.value = false;
    formuSifirla();
    await yukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    kaydediliyor.value = false;
  }
};

watch(() => props.hesap, yukle);
onMounted(yukle);
</script>

<template>
  <div class="p-6">
    <p v-if="!hesap" class="text-sm text-n-slate-11">
      {{ t('META_ADS.ORTAK.HESAP_SECIN') }}
    </p>

    <template v-else>
      <div class="flex items-center gap-2 mb-4">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('META_ADS.KREATIFLER.BASLIK') }}
        </h2>
        <span class="grow" />
        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-refresh-cw"
          :label="t('META_ADS.ORTAK.YENILE')"
          @click="yukle"
        />
        <Button
          size="sm"
          variant="solid"
          icon="i-lucide-plus"
          :label="t('META_ADS.KREATIFLER.YENI')"
          @click="formAcik = !formAcik"
        />
      </div>

      <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>

      <form
        v-if="formAcik"
        class="grid gap-3 p-4 mb-5 border rounded-lg sm:grid-cols-2 border-n-weak"
        @submit.prevent="kreatifOlustur"
      >
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.KREATIFLER.FORM.AD') }}
          <input v-model="form.name" type="text" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.KREATIFLER.FORM.SAYFA_ID') }}
          <input v-model="form.page_id" type="text" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.KREATIFLER.FORM.BAGLANTI') }}
          <input v-model="form.link_url" type="text" placeholder="https://" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.KREATIFLER.FORM.GORSEL_HASH') }}
          <input v-model="form.image_hash" type="text" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.KREATIFLER.FORM.BASLIK_METNI') }}
          <input v-model="form.headline" type="text" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.KREATIFLER.FORM.MESAJ') }}
          <input v-model="form.message" type="text" />
        </label>

        <p class="text-xs sm:col-span-2 text-n-slate-11">
          {{ t('META_ADS.KREATIFLER.FORM.IPUCU') }}
        </p>

        <div class="flex gap-2 sm:col-span-2">
          <Button
            size="sm"
            variant="solid"
            type="submit"
            :is-loading="kaydediliyor"
            :label="t('META_ADS.ORTAK.KAYDET')"
          />
          <Button
            type="button"
            size="sm"
            variant="faded"
            :label="t('META_ADS.ORTAK.VAZGEC')"
            @click="formAcik = false"
          />
        </div>
      </form>

      <AracTablosu
        :satirlar="satirlar"
        :sutunlar="sutunlar"
        :yukleniyor="yukleniyor"
        :bos-metin="t('META_ADS.KREATIFLER.BOS')"
      >
        <template #eylemler="{ satir }">
          <Button
            size="sm"
            variant="faded"
            :label="t('META_ADS.ORTAK.AYRINTI')"
            @click="ayrintiAc(satir)"
          />
          <Button
            size="sm"
            variant="faded"
            class="ml-2"
            :label="t('META_ADS.KREATIFLER.MEDYA')"
            @click="medyaAc(satir)"
          />
          <Button
            size="sm"
            variant="faded"
            class="ml-2"
            :label="t('META_ADS.KREATIFLER.ADI_DEGISTIR')"
            @click="adiDegistir(satir)"
          />
        </template>
      </AracTablosu>

      <div
        v-if="panelAcik"
        class="fixed inset-0 z-50 flex items-center justify-center p-6 bg-n-alpha-black2"
        @click.self="panelAcik = false"
      >
        <div
          class="w-full max-w-2xl max-h-[80vh] overflow-auto rounded-lg bg-n-surface-1 border border-n-weak"
        >
          <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
            <h3 class="text-sm font-medium text-n-slate-12">{{ panelBaslik }}</h3>
            <span class="grow" />
            <Button
              size="sm"
              variant="faded"
              :label="t('META_ADS.ORTAK.KAPAT')"
              @click="panelAcik = false"
            />
          </div>
          <div class="p-4">
            <div v-if="panelYukleniyor" class="flex justify-center p-8">
              <Spinner class="text-n-brand" />
            </div>
            <template v-else>
              <img
                v-if="panelGorsel"
                :src="panelGorsel"
                alt=""
                class="mb-4 border rounded max-h-64 border-n-weak"
              />
              <p
                v-if="panelMetni"
                class="text-sm whitespace-pre-line text-n-slate-11"
              >
                {{ panelMetni }}
              </p>

              <div
                v-for="(kayit, i) in panelKayitlari"
                :key="i"
                class="py-3 border-b last:border-b-0 border-n-weak"
              >
                <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                  <template v-for="alan in kayit" :key="alan.anahtar">
                    <dt class="text-n-slate-11">{{ alan.etiket }}</dt>
                    <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
                  </template>
                </dl>
              </div>

              <p v-if="panelBos" class="py-6 text-sm text-center text-n-slate-11">
                {{ t('META_ADS.KREATIFLER.PANEL.BOS') }}
              </p>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
